import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Modal } from "../../components/ui/Modal";
import { usePermission } from "../../hooks/usePermission";
import type { WeeklyDue, WeeklyDuesMemberOption } from "../../types";

const PAYMENT_STATUS_OPTIONS: { value: "paid" | "unpaid" | "open" | "exempt" | "exempt_permanent"; label: string }[] = [
  { value: "paid", label: "Bezahlt" },
  { value: "unpaid", label: "Nicht bezahlt" },
  { value: "open", label: "Offen" },
  { value: "exempt", label: "Freigestellt" },
  { value: "exempt_permanent", label: "Dauerhaft freigestellt" },
];

interface Props {
  entry: WeeklyDue | null; // null = neuer Eintrag in der aktuellen Woche
  /** Mitglieder, die in der aktuellen Woche bereits enthalten sind (nur relevant beim Neuanlegen). */
  excludeMemberIds?: number[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Vorname/Nachname/Rang werden bewusst NICHT als eigene Formularfelder
 * geführt - sie kommen ausschließlich aus der Mitgliedsauswahl (automatisch
 * aus der bestehenden Mitgliederverwaltung übernommen, siehe
 * /api/weekly-dues/members-list) und werden nie separat gespeichert. Beim
 * Bearbeiten eines bestehenden Eintrags ist das Mitglied fest (ein Eintrag
 * gehört zu genau einem Mitglied in genau einer Woche) - nur Gegenstand und
 * Zahlungsstatus können geändert werden.
 */
export function WeeklyDuesForm({ entry, excludeMemberIds = [], onClose, onSaved }: Props) {
  const isEdit = !!entry;
  const canSetPrepaid = usePermission("weeklydues.prepaid");

  const [members, setMembers] = useState<WeeklyDuesMemberOption[]>([]);
  const [membersLoading, setMembersLoading] = useState(!isEdit);
  const [memberId, setMemberId] = useState(entry?.member_id ? String(entry.member_id) : "");
  const [itemDescription, setItemDescription] = useState(entry?.item_description ?? "");
  const [paymentStatus, setPaymentStatus] = useState<"paid" | "unpaid" | "open" | "exempt" | "exempt_permanent">(entry?.payment_status ?? "open");
  const [prepaidWeeks, setPrepaidWeeks] = useState(String(entry?.prepaid_weeks ?? 0));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit) return;
    api.get<WeeklyDuesMemberOption[]>("/api/weekly-dues/members-list")
      .then((all) => setMembers(all.filter((m) => !excludeMemberIds.includes(m.id))))
      .catch(() => {})
      .finally(() => setMembersLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // prepaidWeeks nur für den Systemadministrator mitsenden (Server prüft zusätzlich).
      const extra = canSetPrepaid ? { prepaidWeeks: Number(prepaidWeeks) || 0 } : {};
      if (isEdit) {
        await api.put(`/api/weekly-dues/${entry!.id}`, { itemDescription, paymentStatus, ...extra });
      } else {
        await api.post("/api/weekly-dues", { memberId: Number(memberId), itemDescription, paymentStatus, ...extra });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isEdit ? `Wochenabgabe bearbeiten — ${entry!.first_name} ${entry!.last_name}` : "Mitglied zur aktuellen Woche hinzufügen"}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" form="weekly-dues-form" disabled={submitting || (!isEdit && !memberId)}>
            {submitting ? "Speichern…" : "Speichern"}
          </button>
        </>
      }
    >
      <form id="weekly-dues-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

        {isEdit ? (
          <div>
            <label className="label">Mitglied</label>
            <p className="text-gray-200 font-medium">
              {entry!.last_name}, {entry!.first_name}{entry!.rank ? ` — ${entry!.rank}` : ""}
            </p>
          </div>
        ) : (
          <div>
            <label className="label">Mitglied *</label>
            <select className="input" value={memberId} onChange={(e) => setMemberId(e.target.value)} required disabled={membersLoading}>
              <option value="">— auswählen —</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.last_name}, {m.first_name}{m.rank ? ` — ${m.rank}` : ""}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Vorname, Nachname und Rang werden automatisch aus der Mitgliederverwaltung übernommen.</p>
          </div>
        )}

        <div>
          <label className="label">Gegenstand</label>
          <input
            className="input"
            value={itemDescription ?? ""}
            onChange={(e) => setItemDescription(e.target.value)}
            placeholder="z.B. Kutte, Patch, Ausrüstung…"
          />
        </div>

        <div>
          <label className="label">Bezahlt ?</label>
          <select className="input" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value as "paid" | "unpaid" | "open" | "exempt" | "exempt_permanent")}>
            {PAYMENT_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {canSetPrepaid && (
          <div>
            <label className="label">Im Voraus bezahlte Wochen (zusätzlich zur aktuellen)</label>
            <input
              className="input w-32"
              type="number"
              min={0}
              max={52}
              step={1}
              value={prepaidWeeks}
              onChange={(e) => setPrepaidWeeks(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">
              Bei jeder „Neuen Woche" wird dieses Mitglied automatisch auf „Bezahlt" gesetzt und der Wert um 1 verringert, bis er 0 erreicht.
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
}
